import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

// Утилитная функция для удаления HTML-тегов
const stripHtml = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Создание span-элемента с кнопкой
const createActionSpan = (action: string, text: string): HTMLSpanElement => {
  const span = document.createElement('span');
  span.className = 'text-text-positive-primary cursor-pointer underline';
  span.setAttribute('data-action', action);
  span.textContent = text;
  return span;
};

// Поиск последнего текстового узла в элементе
const findLastTextNode = (element: Element): Text | null => {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);
  let lastTextNode: Text | null = null;
  let currentNode = walker.nextNode();

  while (currentNode !== null) {
    if (currentNode.textContent?.trim()) {
      lastTextNode = currentNode as Text;
    }
    currentNode = walker.nextNode();
  }

  return lastTextNode;
};

// Простая функция для обрезания HTML
const truncateHtml = (
  html: string,
  maxLength: number,
  readMoreText: string,
): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;

  let charCount = 0;
  const textNodes: Text[] = [];

  // Собираем все текстовые узлы
  const walker = document.createTreeWalker(tmp, NodeFilter.SHOW_TEXT, null);
  let node = walker.nextNode();
  while (node) {
    if (node.textContent?.trim()) {
      textNodes.push(node as Text);
    }
    node = walker.nextNode();
  }

  // Находим точку обрезания
  for (let i = 0; i < textNodes.length; i++) {
    const textNode = textNodes[i];
    const text = textNode.textContent || '';

    if (charCount + text.length <= maxLength) {
      charCount += text.length;
    } else {
      // Обрезаем на этом узле
      const remainingChars = maxLength - charCount;
      const truncatedText = `${text.substring(0, remainingChars)}... `;
      textNode.textContent = truncatedText;

      // Добавляем кнопку
      const readMoreSpan = createActionSpan('read-more', readMoreText);
      if (textNode.parentElement) {
        textNode.parentElement.insertBefore(readMoreSpan, textNode.nextSibling);
      }

      // Удаляем все следующие текстовые узлы и их родительские элементы если они стали пустыми
      for (let j = i + 1; j < textNodes.length; j++) {
        const nodeToRemove = textNodes[j];
        if (nodeToRemove.parentNode) {
          nodeToRemove.parentNode.removeChild(nodeToRemove);

          // Проверяем и удаляем пустые родительские элементы
          let parent = nodeToRemove.parentNode as Element;
          while (
            parent &&
            parent !== tmp &&
            !parent.textContent?.trim() &&
            parent.childNodes.length === 0
          ) {
            const grandParent = parent.parentNode;
            if (grandParent) {
              grandParent.removeChild(parent);
              parent = grandParent as Element;
            } else {
              break;
            }
          }
        }
      }
      break;
    }
  }

  return tmp.innerHTML;
};

// Добавление кнопки "Свернуть" в полное описание
const addReadLessButton = (tmp: Element, readLessText: string): void => {
  const readLessSpan = createActionSpan('read-less', ` ${readLessText}`);
  const lastTextNode = findLastTextNode(tmp);

  if (lastTextNode) {
    lastTextNode.parentElement?.insertBefore(
      readLessSpan,
      lastTextNode.nextSibling,
    );
  } else {
    tmp.appendChild(readLessSpan);
  }
};

export const useTruncatedHtml = (
  htmlContent: () => string | undefined | null,
  maxLength = 220,
) => {
  const { t } = useI18n();
  const isExpanded = ref(false);

  const truncatedDescriptionWithButton = computed(() => {
    const content = htmlContent();
    if (!content) return '';

    const plainText = stripHtml(content);
    if (plainText.length <= maxLength) return content;

    return truncateHtml(content, maxLength, t('common.readMore'));
  });

  const fullDescriptionWithButton = computed(() => {
    const content = htmlContent();
    if (!content) return '';

    const plainText = stripHtml(content);
    if (plainText.length <= maxLength) return content;

    const tmp = document.createElement('div');
    tmp.innerHTML = content;
    addReadLessButton(tmp, t('common.readLess'));

    return tmp.innerHTML;
  });

  const shouldShowReadMore = computed(() => {
    const content = htmlContent();
    if (!content) return false;
    return stripHtml(content).length > maxLength;
  });

  const toggleExpanded = () => {
    isExpanded.value = !isExpanded.value;
  };

  const handleDescriptionClick = (event: Event) => {
    const target = event.target as HTMLElement;
    const action = target.getAttribute('data-action');
    if (action === 'read-more' || action === 'read-less') {
      toggleExpanded();
    }
  };

  return {
    isExpanded,
    truncatedDescriptionWithButton,
    fullDescriptionWithButton,
    shouldShowReadMore,
    toggleExpanded,
    handleDescriptionClick,
  };
};
